import { NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { useCallback, useState } from "react";
import { NAV_GROUPS } from "./nav";
import { useHasRole, useSession } from "@/shared/auth/useSession";
import { roleAtLeast } from "@/shared/api/schemas/common";
import { useAuthStore } from "@/shared/auth/store";
import { useToast } from "@/shared/ui/Toast";
import { Icon } from "@/shared/ui/Icon";
import styles from "./AppLayout.module.css";

function NavItemLink({
  item,
  onNavigate,
}: {
  item: (typeof NAV_GROUPS)[number]["items"][number];
  onNavigate: () => void;
}) {
  const allowed = useHasRole(item.minimum);
  if (!allowed) return null;
  return (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      className={({ isActive }) =>
        isActive ? `${styles.navLink} ${styles.navLinkActive}` : styles.navLink
      }
    >
      <Icon name={item.icon} />
      <span>{item.label}</span>
    </NavLink>
  );
}
function NavSection({
  group,
  onNavigate,
}: {
  group: (typeof NAV_GROUPS)[number];
  onNavigate: () => void;
}) {
  const { staff } = useSession();
  if (!group.items.some((item) => staff !== null && roleAtLeast(staff.role, item.minimum)))
    return null;
  return (
    <div className={styles.navGroup}>
      <div className={styles.navGroupTitle}>{group.title}</div>
      {group.items.map((item) => (
        <NavItemLink key={item.to} item={item} onNavigate={onNavigate} />
      ))}
    </div>
  );
}
export function AppLayout() {
  const { staff } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const { notify } = useToast();
  const [menuOpen, setMenuOpen] = useState(false);
  const currentGroup = NAV_GROUPS.find((group) =>
    group.items.some((item) => location.pathname === item.to),
  );
  const current = currentGroup?.items.find((item) => item.to === location.pathname);
  const roleLabels = {
    owner: "Владелец",
    admin: "Администратор",
    editor: "Редактор",
    analyst: "Аналитик",
    viewer: "Наблюдатель",
  };
  const handleLogout = useCallback(async () => {
    try {
      await fetch("/admin/api/auth/logout", { method: "POST", credentials: "include" });
    } finally {
      useAuthStore.getState().clear();
      notify("info", "Вы вышли из системы");
      void navigate("/login", { replace: true });
    }
  }, [navigate, notify]);
  return (
    <div className={styles.shell}>
      <a href="#main-content" className={styles.skipLink}>
        К содержимому
      </a>
      <aside
        className={styles.sidebar}
        aria-label="Панель управления"
        onKeyDown={(event) => {
          if (event.key === "Escape" && menuOpen) {
            setMenuOpen(false);
            document.querySelector<HTMLButtonElement>(`.${styles.menuToggle}`)?.focus();
          }
        }}
      >
        <div className={styles.brandRow}>
          <NavLink to="/" className={styles.brand} aria-label="shy — главная">
            shy.
          </NavLink>
          <span className={styles.brandCaption}>studio admin</span>
          <button
            className={styles.menuToggle}
            aria-expanded={menuOpen}
            aria-controls="admin-navigation"
            aria-label={menuOpen ? "Свернуть меню" : "Открыть меню"}
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <Icon name={menuOpen ? "close" : "menu"} />
          </button>
        </div>
        <div
          id="admin-navigation"
          className={`${styles.sidebarBody} ${menuOpen ? styles.sidebarBodyOpen : ""}`}
        >
          <nav className={styles.nav} aria-label="Основная навигация">
            {NAV_GROUPS.map((group) => (
              <NavSection key={group.title} group={group} onNavigate={() => setMenuOpen(false)} />
            ))}
          </nav>
          <div className={styles.account}>
            <div className={styles.avatar}>{staff?.email?.slice(0, 1).toUpperCase() ?? "S"}</div>
            <div className={styles.accountDetails}>
              <div className={styles.accountEmail} title={staff?.email}>
                {staff?.email}
              </div>
              <div className={styles.accountRole}>{staff ? roleLabels[staff.role] : ""}</div>
            </div>
            <button
              className={styles.logout}
              title="Выйти"
              aria-label="Выйти"
              onClick={() => void handleLogout()}
            >
              <Icon name="logout" size={18} />
            </button>
          </div>
        </div>
      </aside>
      <div className={styles.main}>
        <header className={styles.topbar}>
          <span>
            {currentGroup?.title ?? "Рабочее пространство"}
            <span className={styles.breadcrumbDivider}>/</span>
            <strong>{current?.label ?? "shy"}</strong>
          </span>
          <span className={styles.workspaceLabel}>shy workspace</span>
        </header>
        <main id="main-content" tabIndex={-1} className={styles.content}>
          <Outlet />
        </main>
        <footer className={styles.footer}>
          shy. <span>Пространство управления приложением</span>
        </footer>
      </div>
    </div>
  );
}
